import React, { useState, useEffect } from 'react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { Icon } from '@iconify/react';
import { getTraineeAnalytics } from '../../../services/traineeService';
import { toast } from 'react-toastify';

function TraineeAnalyticsCourseTab({ traineeId }) {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [courseData, setCourseData] = useState([]);
  const [expandedCourse, setExpandedCourse] = useState(null);
  
  // States for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const response = await getTraineeAnalytics({ trainee_id: traineeId });
      // console.log('Course Data---------------------:', response.data.course_completion);

      if (response.success) {
        const data = response.data.course_completion || [];
        setCourseData(data);
        setFilteredData(data);
      } else {
        toast.error(response.message || 'Failed to fetch course data');
      }
    } catch (error) {
      console.error('Error fetching course data:', error);
      toast.error('Something went wrong while fetching course data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (traineeId) {
      fetchCourseData();
    }
  }, [traineeId]);

  // Handle search with debounce
  useEffect(() => {
    setSearchLoading(true);
    const searchTimeout = setTimeout(() => {
      const filtered = courseData.filter(course =>
        course.course_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.course_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
      setCurrentPage(1);
      setSearchLoading(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, courseData]);

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const toggleCourseExpand = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[220px] rounded-2xl border border-gray-100 bg-gray-50/40">
        <Loader />
      </div>
    );
  }

  if (!courseData || courseData.length === 0) {
    return <NoData message="No courses found" />;
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative w-full sm:max-w-sm">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <Icon icon="fluent:search-24-regular" width="18" height="18" />
            </span>
            <input
              type="search"
              className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm bg-white"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <span className="inline-block h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-2">
            <div className="text-xs font-semibold text-gray-500">Rows</div>
            <select
              className="px-3 py-2 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 focus:outline-none"
              value={recordsPerPage}
              onChange={handleRecordsPerPageChange}
              disabled={searchLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <div className="text-sm text-gray-500">
              Total: <span className="font-semibold text-gray-900">{filteredData.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-[1040px] w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="text-left text-xs font-semibold text-gray-600">
              <th className="px-4 py-3 w-[90px]">SL No.</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3 w-[110px]">Type</th>
              <th className="px-4 py-3 w-[220px]">Progress</th>
              <th className="px-4 py-3 w-[140px]">Status</th>
              <th className="px-4 py-3 w-[120px] text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {searchLoading ? (
              <tr>
                <td colSpan="6" className="text-center py-10">
                  <Loader />
                </td>
              </tr>
            ) : currentRecords.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-0">
                  <NoData message={searchTerm ? `No courses found for "${searchTerm}"` : "No courses available"} />
                </td>
              </tr>
            ) : (
              currentRecords.map((course, index) => (
                <React.Fragment key={course.course_id}>
                  <tr className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 text-gray-500 text-center">{indexOfFirstRecord + index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <img
                          src={course.banner_image}
                          alt={course.course_name}
                          className="rounded-xl border border-gray-100 shadow-sm"
                          style={{
                            height: "40px",
                            width: '60px',
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/60x40?text=No+Image';
                          }}
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 mb-1 truncate" title={course.course_name || ''}>{course.course_name}</div>
                          <div className="flex items-center flex-wrap gap-3 text-xs text-gray-500">
                            <div className="flex items-center" title="Videos">
                              <Icon icon="fluent:video-24-regular" className="text-gray-400" width="14" />
                              <span>Videos: <span className="font-semibold text-gray-700">{course.total_video}</span></span>
                            </div>
                            <div className="flex items-center" title="Documents">
                              <Icon icon="fluent:document-24-regular" className="text-gray-400" width="14" />
                              <span>Documents: <span className="font-semibold text-gray-700">{course.total_document}</span></span>
                            </div>
                            <div className="flex items-center" title="Assessments">
                              <Icon icon="fluent:task-list-square-24-regular" className="text-gray-400" width="14" />
                              <span>Assessments: <span className="font-semibold text-gray-700">{course.total_assessment}</span></span>
                            </div>
                            <div className="flex items-center" title="Surveys">
                              <Icon icon="fluent:form-24-regular" className="text-gray-400" width="14" />
                              <span>Surveys: <span className="font-semibold text-gray-700">{course.total_survey}</span></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-700 capitalize">
                        {course.course_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {parseFloat(course.completion_percentage).toFixed(1)}%
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full mt-2" style={{ height: '8px' }}>
                        <div
                          className={`h-full rounded-full ${course.is_completed ? 'bg-green-500' : 'bg-primary'}`}
                          role="progressbar"
                          style={{
                            width: `${Math.min(course.completion_percentage, 100)}%`,
                            transition: 'width 0.3s ease'
                          }}
                          aria-valuenow={Math.min(course.completion_percentage, 100)}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${course.is_completed ? 'bg-green-50 border border-green-500 text-green-700' : 'bg-amber-50 border border-amber-500 text-amber-700'}`}>
                        {course.is_completed ? 'Completed' : 'In Progress'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => toggleCourseExpand(course.course_id)}
                          title={course.assessment_results && course.assessment_results.length > 0 ? 'View Assessments' : 'No Assessments'}
                          disabled={!course.assessment_results || course.assessment_results.length === 0}
                        >
                          <Icon
                            icon={expandedCourse === course.course_id ? 'fluent:chevron-up-24-regular' : 'fluent:task-list-square-24-regular'}
                            width="16"
                            height="16"
                            className={!course.assessment_results || course.assessment_results.length === 0 ? 'text-gray-400' : 'text-gray-700'}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedCourse === course.course_id && course.assessment_results.length > 0 && (
                    <tr>
                      <td colSpan="6" className="p-0">
                        <div className="bg-gray-50 p-4">
                          <h6 className="mb-3 font-semibold text-gray-900">Assessment Results</h6>
                          <div className="overflow-x-auto">
                            <table className="min-w-[640px] w-full text-sm mb-0">
                              <thead className="bg-white">
                                <tr className="text-left text-xs font-semibold text-gray-600">
                                  <th className="px-3 py-2">Assessment Name</th>
                                  <th className="px-3 py-2 w-[120px]">Type</th>
                                  <th className="px-3 py-2 w-[140px]">Last Attempt</th>
                                  <th className="px-3 py-2 w-[180px]">Score</th>
                                  <th className="px-3 py-2 w-[140px]">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {course.assessment_results.map((assessment) => (
                                  <tr key={assessment.assessment_id}>
                                    <td className="px-3 py-2 font-medium text-gray-900">{assessment.assessment_name}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${assessment.assessment_type === 'assessment' ? 'bg-blue-100 border border-blue-500 text-blue-500' : 'bg-gray-100 border border-gray-500 text-gray-500'} p-2`}>
                                        {assessment.assessment_type}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">{new Date(assessment.last_attempt_date).toLocaleDateString()}</td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {assessment.assessment_type === 'assessment' ? (
                                        <>
                                          {assessment.correct_answers}/{assessment.total_questions}
                                          <span className="text-gray-500 ml-2">({assessment.score_percentage}%)</span>
                                        </>
                                      ) : (
                                        '--'
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {assessment.assessment_type === 'assessment' && (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${assessment.status === 'Passed' ? 'bg-green-100 border border-green-500 text-green-500' : 'bg-red-100 border border-red-500 text-red-500'} p-2`}>
                                          {assessment.status}
                                        </span>
                                      )}
                                      {assessment.assessment_type === 'survey' && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 border border-green-500 text-green-500 p-2">Completed</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 sm:px-5 py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-500">
          Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
          <span className="font-semibold text-gray-900">{totalPages || 1}</span>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={currentPage === 1 || searchLoading}
            className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || searchLoading}
            className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default TraineeAnalyticsCourseTab;